import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { verificationApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { useAuthStore } from '../../stores/authStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';

const otpSchema = z.object({
  code: z.string().length(6, 'OTP must be 6 digits'),
});

type OtpFormData = z.infer<typeof otpSchema>;

interface OtpStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function OtpStep({ onNext, onBack }: OtpStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { requestId, setPhoneVerified, setStep } = useVerificationStore();
  const user = useAuthStore((state) => state.user);
  const phoneNumber = user?.phoneNumber || '';
  const hasSentInitialOtp = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
  });

  useEffect(() => {
    // Auto-send OTP on mount (ref guard prevents double-send in React Strict Mode)
    if (hasSentInitialOtp.current) return;
    hasSentInitialOtp.current = true;
    sendOtp();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendOtp = async () => {
    if (!requestId || !phoneNumber) return;

    setIsSending(true);
    setError(null);

    try {
      await verificationApi.sendOtp(requestId, phoneNumber);
      setSuccess(`OTP sent to ${maskPhoneNumber(phoneNumber)}`);
      setCountdown(60);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to send OTP');
    } finally {
      setIsSending(false);
    }
  };

  const onSubmit = async (data: OtpFormData) => {
    if (!requestId || !phoneNumber) return;

    setError(null);
    setIsLoading(true);

    try {
      await verificationApi.verifyOtp(requestId, phoneNumber, data.code);
      setPhoneVerified(true);
      setStep('id-upload');
      onNext();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Invalid OTP code');
    } finally {
      setIsLoading(false);
    }
  };

  const maskPhoneNumber = (phone: string) => {
    if (!phone || phone.length < 6) return '***';
    return phone.slice(0, 4) + '****' + phone.slice(-3);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Verify Your Phone Number</h2>
        <p className="text-sm text-gray-600 mt-1">
          We've sent a 6-digit verification code to {maskPhoneNumber(phoneNumber)}
        </p>
      </div>

      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="code" className="label">
            Verification Code
          </label>
          <input
            id="code"
            type="text"
            maxLength={6}
            placeholder="Enter 6-digit code"
            className={`input text-center text-2xl tracking-widest ${
              errors.code ? 'input-error' : ''
            }`}
            {...register('code')}
          />
          {errors.code && <p className="error-text">{errors.code.message}</p>}
        </div>

        <div className="text-center">
          {countdown > 0 ? (
            <p className="text-sm text-gray-500">
              Resend code in {countdown} seconds
            </p>
          ) : (
            <button
              type="button"
              onClick={sendOtp}
              disabled={isSending}
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              {isSending ? 'Sending...' : "Didn't receive a code? Resend"}
            </button>
          )}
        </div>

        <div className="flex justify-between pt-4">
          <button type="button" onClick={onBack} className="btn-secondary">
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" /> : 'Verify'}
          </button>
        </div>
      </form>
    </div>
  );
}
